import { useEffect, type JSX } from "react";
import {
  LayoutDashboard,
  Milestone,
  ChartNoAxesCombined,
  Landmark,
  Calculator,
  Wallet,
  History,
  Warehouse,
} from "lucide-react";
import { hatRecht, SEITEN_RECHTE, type Seite } from "../../state/berechtigungen.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { merke, zielFuerReiter } from "../../state/reiterGedaechtnis.ts";
import { Verweis } from "../Verweis.tsx";

/**
 * Die Seiten der SPA EINSCHLIESSLICH der Lieferantenflaeche (Task 11, Fix-Runde 1 vom 2026-08-11).
 *
 * WARUM NICHT IN `state/berechtigungen.ts`: `test/parity/berechtigungen.test.ts` haelt jenes Modul
 * WORTGLEICH gegen `index.html` - `Seite`, `SEITEN_RECHTE` und `ersteErlaubteSeite` sind dort
 * Abschriften von `PAGE_PERMISSIONS`/`firstAllowedPage` (`index.html:5278-5308`). Der Bestand kennt
 * `"suppliers"` nicht (`showPage`, `index.html:6332`, fuehrt genau sechs Seiten), ein Eintrag dort
 * waere also ein Waechterbruch und keine Ergaenzung.
 *
 * WARUM DIESE UNION TROTZDEM HIER LEBT, obwohl unten kein Reiter mehr dafuer steht: `AktiveSeite`
 * ist die Antwort auf "welche Seite markiert die Kopfzeile gerade", und das ist ein
 * Navigationsbegriff. Der Bestand fuehrt denselben Fall bereits: `users` steht in `Seite`, hat aber
 * keinen `nav-users`-Reiter (`index.html:3671-3677`) und wird ausschliesslich ueber das Marken-Menue
 * geoeffnet. `"suppliers"` ist seit der Fix-Runde 1 genau derselbe Fall - eine Seite ohne Reiter.
 *
 * `ersteErlaubteSeite` kennt die Seite folgerichtig weiterhin nicht: sie ist nie Startseite.
 */
export type AktiveSeite =
  | Seite
  | "suppliers"
  | "liquidity"
  | "paymentTemplates"
  | "activities"
  | "dashboard"
  | "kiErlauben"
  | "kiZugaenge";

/**
 * Kennung der Lieferantenseite - an EINER Stelle, `App.tsx` (Einhaengepunkt) und die Tests lesen
 * dieselbe. Die BESCHRIFTUNG steht seit der Fix-Runde 1 nicht mehr hier, sondern dort, wo die
 * Flaeche geoeffnet wird: `MarkenMenue.tsx`, neben "Nutzerverwaltung öffnen".
 */
export const LIEFERANTEN_SEITE = "suppliers" as const;

/**
 * Kennung der Liquiditaetsplanung. Dieselbe Begruendung wie bei `suppliers`: die Seite hat kein
 * Vorbild in `index.html`, `Seite`/`SEITEN_RECHTE` in `state/berechtigungen.ts` bleiben deshalb
 * wortgleich zum Bestand (`test/parity/berechtigungen.test.ts`). Anders als die Lieferanten hat sie
 * einen Reiter und eine Rechteanforderung - beide leben hier (`REITER`, `LIQUIDITAET_RECHT`).
 */
export const LIQUIDITAET_SEITE = "liquidity" as const;

/** Bestehendes Recht der Zahlungsseite: die Daten kommen aus `/api/fx-deals` und `/api/fx-overview`. */
export const LIQUIDITAET_RECHT = "view_payments";

/**
 * Kennung der Seite "Aktivitaeten" (Protokoll aller Aenderungen, URL `/aktivitaeten`). Wie die
 * Liquiditaetsplanung eine Seite ohne Vorbild in `index.html` - `Seite`/`SEITEN_RECHTE` in
 * `state/berechtigungen.ts` bleiben wortgleich zum Bestand. Anders als jene hat sie KEINE
 * Rechteanforderung: `rechtFuerReiter` liefert `undefined`, der Reiter ist fuer jede Rolle sichtbar
 * (die Eintraege selbst stammen aus dem Protokoll, das serverseitig lesbar ist).
 */
export const AKTIVITAETEN_SEITE = "activities" as const;

/**
 * Kennung der Seite "Dashboard" (Lagerplanung: anstehende Anlieferungen aus China, URL `/dashboard`). Wie die Aktivitaeten eine
 * Seite ohne Vorbild in `index.html` und ohne Rechteanforderung: `rechtFuerReiter` liefert `undefined`, der Reiter ist fuer jede
 * Rolle sichtbar. Die Seite rechnet nur aus dem Bestand, den die App ohnehin hat (`App.tsx`: `SEITEN_MIT_BESTAND`).
 */
export const DASHBOARD_SEITE = "dashboard" as const;

/**
 * Seiten mit Reiter: die fuenf Bestandsseiten ohne `users` plus Liquiditaetsplanung, Aktivitaeten und Dashboard.
 */
type Reiterseite =
  | Exclude<Seite, "users">
  | typeof LIQUIDITAET_SEITE
  | typeof AKTIVITAETEN_SEITE
  | typeof DASHBOARD_SEITE;

/** `undefined` = freie Seite, der Reiter steht ohne Recht. */
function rechtFuerReiter(seite: Reiterseite): string | undefined {
  if (seite === AKTIVITAETEN_SEITE || seite === DASHBOARD_SEITE) return undefined;
  return seite === LIQUIDITAET_SEITE ? LIQUIDITAET_RECHT : SEITEN_RECHTE[seite];
}

/**
 * Kennung der Seite "Zahlungsvorlagen" (Nutzerentscheidung 01.10.2026): Einsehen, Bearbeiten,
 * Loeschen und Anlegen der Zahlungsvorlagen; am Lieferanten werden sie nur noch ausgewaehlt. Wie die
 * Lieferantenseite eine Seite ohne Reiter, erreichbar ueber das Marken-Menue und ueber Links aus der
 * Lieferantenmaske (`/zahlungsvorlagen/<id>`).
 */
export const ZAHLUNGSVORLAGEN_SEITE = "paymentTemplates" as const;

/**
 * Kennungen der beiden Seiten des KI-Zugangs (Stufe 1, nur lesend): die Erlauben-Seite (`/ki-erlauben?anfrage=<id>`,
 * dorthin leitet ein KI-Werkzeug den Kollegen) und "Meine KI-Zugaenge" (Marken-Menue). Wie die Lieferantenseite
 * ohne Reiter und ohne Rechteanforderung: jedes Konto verbindet und widerruft seine eigenen Zugaenge, nur
 * "Alle Verbindungen" haengt an `manage_users` und wird auf der Seite selbst entschieden. Sie liegen als
 * nachgeladener Chunk hinter `KiZugang/KiZugangLazy.tsx`.
 */
export const KI_ERLAUBEN_SEITE = "kiErlauben" as const;
export const KI_ZUGAENGE_SEITE = "kiZugaenge" as const;

export interface NavigationProps {
  seite: AktiveSeite;
  rechte: ReadonlySet<string>;
}

/**
 * Die Reitergruppe (`header > nav`, index.html:3671-3677). Reihenfolge und Beschriftungen wörtlich
 * übernommen - `users` hat hier absichtlich keinen Reiter (kein `nav-users` im Original, die
 * Nutzerverwaltung öffnet ausschließlich über das Marken-Menü, siehe `MarkenMenue.tsx`).
 *
 * FIX-RUNDE 1 ZU TASK 11 (Entscheidung des Auftraggebers, 2026-08-11): dasselbe gilt seither für die
 * Lieferantenfläche. Sie stand kurzzeitig als SECHSTER Reiter hier; der Auftraggeber hat entschieden,
 * dass sie in das Marken-Menü gehört - dorthin, wo Nutzerverwaltung und Aufgabenvorlagen liegen. Sie
 * ist Verwaltung, keine tägliche Arbeitsansicht. Diese Liste führt damit wieder GENAU die fünf
 * Bestandsreiter, und die Reitergruppe ist wieder pixelgleich zum Bestand (`nav` steht per
 * `position: absolute; left: 50%` zentriert - ein sechster Reiter verschob ALLE FÜNF, gemessen mit
 * 7705 bzw. 7329 abweichenden Pixeln; siehe Fix-Runden-Bericht).
 *
 * Rechteprüfung per bedingtem Rendering (nicht `style.display`, wie das Original es über
 * `updateUserUi()` tut, index.html:5386-5388): ein per CSS ausgeblendeter Reiter bliebe für
 * `getByText`/`queryByText` weiterhin auffindbar, der Komponententest (Schritt 2 des Task-Briefs)
 * verlangt aber `queryByText(...)).toBeNull()` für einen fehlenden Reiter - das geht nur, wenn das
 * Element tatsächlich nicht im DOM steht.
 */
const REITER: { seite: Reiterseite; Icon: typeof LayoutDashboard; label: string }[] = [
  { seite: "board", Icon: LayoutDashboard, label: "Bestellboard" },
  { seite: "timeline", Icon: Milestone, label: "Zeitstrahl" },
  { seite: DASHBOARD_SEITE, Icon: Warehouse, label: "Dashboard" },
  { seite: "overview", Icon: ChartNoAxesCombined, label: "Übersicht" },
  { seite: AKTIVITAETEN_SEITE, Icon: History, label: "Aktivitäten" },
  { seite: "payments", Icon: Landmark, label: "Zahlungen & Kurssicherung" },
  { seite: LIQUIDITAET_SEITE, Icon: Wallet, label: "Liquiditätsplanung" },
  { seite: "costs", Icon: Calculator, label: "Kosten" },
];

/**
 * Kleinbefund der Pruefrunde 2 zu Aufgabe 5: `onSeite` war hier zunaechst nur zu `_onSeite` umbenannt
 * statt entfernt worden. Der Reiter ist seit Aufgabe 5 ein echter `<Verweis>` - react-router navigiert
 * selbst, ein `onClick` braucht es fuer den Seitenwechsel nicht mehr (siehe Kopfkommentar von
 * `Verweis.tsx`, "der Grund, warum es ein ANKER sein muss"). Der Rueckruf ist jetzt vollstaendig aus
 * der Schnittstelle entfernt - `Kopfzeile.tsx` und `App.tsx` reichen ihn nicht mehr durch.
 */
export function Navigation({ seite, rechte }: NavigationProps): JSX.Element {
  const { ansicht } = useAnsicht();
  // Reitergedaechtnis (Zusatzauftrag zu Aufgabe 5): schreibt bei JEDER Ansichtsaenderung, nicht nur
  // bei einem Reiterklick - so traegt das Gedaechtnis auch einen Filter nach, den der Nutzer waehrend
  // des Aufenthalts auf der Seite gesetzt hat. GELESEN wird es unten ausschliesslich fuers Reiterziel,
  // nie fuer `ansicht` selbst - die bleibt allein aus der Adresse (`reiterGedaechtnis.ts`, Bedingung 1).
  useEffect(() => {
    merke(ansicht);
  }, [ansicht]);

  return (
    <nav>
      {REITER.filter((reiter) => {
        const recht = rechtFuerReiter(reiter.seite);
        return recht === undefined || hatRecht(rechte, recht);
      }).map(({ seite: zielSeite, Icon, label }) => (
        <Verweis
          key={zielSeite}
          id={`nav-${zielSeite}`}
          ziel={zielFuerReiter(zielSeite, ansicht)}
          className={zielSeite === seite ? "active" : undefined}
        >
          <Icon aria-hidden="true" strokeWidth={1.8} />
          {label}
        </Verweis>
      ))}
    </nav>
  );
}
